/**
 * Brand Detail Page (US-023.1 + US-023.4)
 *
 * Shows the full brand profile: identity, mission/vision, core values,
 * NIL program details, featured athletes, stats, and public contact
 * channels. PR-D will add a match breakdown section to this page.
 */

import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@app/ui';
import { PageTransition } from '@app/ui/motion';
import { formatCurrency } from '@app/validators';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { RouteErrorComponent } from '../../../components/route-error';
import { api } from '../../../lib/api';
import { isFeatureGateError } from '../../../lib/feature-gate-client';

export const Route = createFileRoute('/_authenticated/brands/$brandId')({
  loader: async ({ context, params }) => {
    const { getServerApi } = await import('../../../lib/api-server');
    const serverApi = await getServerApi();
    await Promise.all([
      context.queryClient.ensureQueryData(
        serverApi.brand.get.queryOptions({ input: { id: params.brandId } }),
      ),
      // `match.forBrand` is gated on the `brandMatching` plan feature; a free
      // user hits FORBIDDEN here. Swallow the gate error so the brand profile
      // still renders — the component drops to an upsell card in place of
      // the match-breakdown panel. Re-throw anything else (real failures
      // still surface the error boundary).
      context.queryClient
        .ensureQueryData(
          serverApi.match.forBrand.queryOptions({ input: { brandId: params.brandId } }),
        )
        .catch((err) => {
          if (!isFeatureGateError(err)) throw err;
        }),
    ]);
  },
  pendingComponent: () => (
    <div className="max-w-4xl animate-pulse space-y-6">
      <div className="h-8 bg-muted rounded w-64" />
      <div className="h-32 bg-muted rounded" />
      <div className="h-64 bg-muted rounded" />
    </div>
  ),
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  component: BrandDetail,
});

function BrandDetail() {
  const { brandId } = Route.useParams();
  const { data: brand } = useSuspenseQuery(
    api.brand.get.queryOptions({ input: { id: brandId } }),
  );
  // useQuery (not Suspense) so a FEATURE_GATE on `match.forBrand` doesn't
  // crash the page — the loader has already swallowed that case for free
  // users. The render block branches explicitly on each state so we don't
  // accidentally fall through to the "complete your profile" card when the
  // real outcome is plan-gated, errored, or still loading.
  const { data: match, error: matchError, isLoading: matchLoading } = useQuery(
    api.match.forBrand.queryOptions({ input: { brandId } }),
  );
  const matchGated = matchError !== null && isFeatureGateError(matchError);
  const matchErrored = matchError !== null && !matchGated;

  return (
    <PageTransition className="max-w-4xl">
      <Link
        to="/brands"
        search={{ sortBy: 'totalDeals', page: 1 }}
        className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4"
      >
        ← Back to brand directory
      </Link>

      {/* Header */}
      <header className="flex items-start gap-4 mb-6">
        {brand.logo ? (
          <img
            src={brand.logo}
            alt=""
            className="w-16 h-16 rounded-full object-cover shrink-0"
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-xl font-semibold shrink-0">
            {brand.name.charAt(0)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold">{brand.name}</h1>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm text-muted-foreground">
            {brand.industry && (
              <span className="capitalize">{brand.industry.replace(/_/g, ' ')}</span>
            )}
            {brand.parentCompany && <span>• {brand.parentCompany}</span>}
            {brand.founded && <span>• Founded {brand.founded}</span>}
            {brand.headquarters && <span>• {brand.headquarters}</span>}
          </div>
          {brand.website && (
            <a
              href={brand.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline mt-1 inline-block"
            >
              {brand.website.replace(/^https?:\/\//, '')}
            </a>
          )}
        </div>
      </header>

      <div className="space-y-6">
        {/* Match score + breakdown (US-023.7) */}
        {matchGated ? (
          // Plan-gated: same slot, upsell card instead of crashing the route.
          <Card>
            <CardHeader>
              <CardTitle>Your Match Score</CardTitle>
              <CardDescription>
                Brand-match scoring is part of the Pro plan. Upgrade to see how this
                brand aligns with your values, sport, and audience.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/pricing" search={{ limit: 'brandMatching' }}>
                <Button size="sm">See Pro plans</Button>
              </Link>
            </CardContent>
          </Card>
        ) : matchErrored ? (
          // Non-gate failure (network, 500): surface it instead of pretending
          // the user has an incomplete profile. The error boundary handles
          // catastrophic loader failures; this branch is for in-component
          // refetch failures so the rest of the brand profile stays usable.
          <Card>
            <CardHeader>
              <CardTitle>Match score unavailable</CardTitle>
              <CardDescription>
                We couldn't load your match for this brand. Try refreshing the page.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : matchLoading ? (
          <Card>
            <CardHeader>
              <CardTitle>Your Match Score</CardTitle>
              <CardDescription>Loading your match…</CardDescription>
            </CardHeader>
          </Card>
        ) : match?.scorable ? (
          <MatchBreakdownCard breakdown={match.breakdown} />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Your Match Score</CardTitle>
              <CardDescription>
                Fill out your profile to see how well this brand aligns with your values,
                sport, and preferences.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/profile">
                <Button size="sm">Complete your profile</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Mission + Vision */}
        {(brand.mission || brand.vision) && (
          <Card>
            <CardHeader>
              <CardTitle>Mission & Vision</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {brand.mission && (
                <div>
                  <p className="text-xs font-medium uppercase text-muted-foreground mb-1">
                    Mission
                  </p>
                  <p className="italic">"{brand.mission}"</p>
                </div>
              )}
              {brand.vision && (
                <div>
                  <p className="text-xs font-medium uppercase text-muted-foreground mb-1">
                    Vision
                  </p>
                  <p className="italic">"{brand.vision}"</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Core Values */}
        {brand.coreValues.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Core Values</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {brand.coreValues.map((v: string) => (
                  <span
                    key={v}
                    className="rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary"
                  >
                    {v}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* NIL Program */}
        {(brand.nilProgramName || brand.nilStrategy || brand.sportsFocus.length > 0) && (
          <Card>
            <CardHeader>
              <CardTitle>NIL Program</CardTitle>
              {brand.nilProgramName && (
                <CardDescription>
                  {brand.nilProgramName}
                  {brand.nilLaunchYear && ` • Launched ${brand.nilLaunchYear}`}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {brand.nilStrategy && (
                <p className="text-sm text-muted-foreground">{brand.nilStrategy}</p>
              )}

              {brand.sportsFocus.length > 0 && (
                <div>
                  <p className="text-xs font-medium uppercase text-muted-foreground mb-2">
                    Sports Focus
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {brand.sportsFocus.map((s: string) => (
                      <span
                        key={s}
                        className="rounded-full bg-secondary px-2 py-0.5 text-xs text-secondary-foreground capitalize"
                      >
                        {s.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {brand.dealTypes.length > 0 && (
                <div>
                  <p className="text-xs font-medium uppercase text-muted-foreground mb-2">
                    Deal Types
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {brand.dealTypes.map((t: string) => (
                      <span
                        key={t}
                        className="rounded-full bg-secondary px-2 py-0.5 text-xs text-secondary-foreground capitalize"
                      >
                        {t.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {(brand.dealRangeMin != null || brand.dealRangeMax != null) && (
                <div>
                  <p className="text-xs font-medium uppercase text-muted-foreground mb-1">
                    Typical Deal Range
                  </p>
                  <p className="text-sm">
                    {brand.dealRangeMin != null ? formatCurrency(brand.dealRangeMin) : '—'}
                    {' – '}
                    {brand.dealRangeMax != null ? formatCurrency(brand.dealRangeMax) : '—'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Featured Athletes */}
        {brand.featuredAthletes.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Featured Athletes</CardTitle>
              <CardDescription>
                Known NIL partnerships this brand has published
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="divide-y">
                {brand.featuredAthletes.map((a, i) => (
                  <li key={`${a.name}-${i}`} className="py-2 flex flex-wrap gap-x-3 items-baseline">
                    <span className="font-medium">{a.name}</span>
                    <span className="text-sm text-muted-foreground capitalize">
                      {a.sport.replace(/_/g, ' ')}
                    </span>
                    <span className="text-sm text-muted-foreground">• {a.school}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <Card>
          <CardHeader>
            <CardTitle>Stats</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <dt className="text-xs font-medium uppercase text-muted-foreground">Total Deals</dt>
                <dd className="text-lg font-semibold">{brand.totalDeals}</dd>
              </div>
              {brand.avgDealSize != null && (
                <div>
                  <dt className="text-xs font-medium uppercase text-muted-foreground">
                    Avg Deal Size
                  </dt>
                  <dd className="text-lg font-semibold">{formatCurrency(brand.avgDealSize)}</dd>
                </div>
              )}
              {brand.dealRangeMax != null && (
                <div>
                  <dt className="text-xs font-medium uppercase text-muted-foreground">
                    Largest Deal
                  </dt>
                  <dd className="text-lg font-semibold">{formatCurrency(brand.dealRangeMax)}</dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>

        {/* Ways to reach this brand (US-023.4) */}
        {hasAnyContact(brand) && (
          <Card>
            <CardHeader>
              <CardTitle>Ways to Reach This Brand</CardTitle>
              <CardDescription>
                Publicly available contact channels. Outreach is yours to make —
                we don't guarantee a response.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-3">
                {brand.partnershipEmail && (
                  <ContactLink
                    label="Partnerships"
                    value={brand.partnershipEmail}
                    href={`mailto:${brand.partnershipEmail}`}
                  />
                )}
                {brand.prEmail && (
                  <ContactLink
                    label="Press / Media"
                    value={brand.prEmail}
                    href={`mailto:${brand.prEmail}`}
                  />
                )}
                {brand.nilContactUrl && (
                  <ContactLink
                    label="NIL Interest Form"
                    value={brand.nilContactUrl.replace(/^https?:\/\//, '')}
                    href={brand.nilContactUrl}
                    external
                  />
                )}
                {brand.pressPageUrl && (
                  <ContactLink
                    label="Press Page"
                    value={brand.pressPageUrl.replace(/^https?:\/\//, '')}
                    href={brand.pressPageUrl}
                    external
                  />
                )}
                {brand.instagramHandle && (
                  <ContactLink
                    label="Instagram"
                    value={`@${brand.instagramHandle}`}
                    href={`https://instagram.com/${brand.instagramHandle}`}
                    external
                  />
                )}
                {brand.twitterHandle && (
                  <ContactLink
                    label="Twitter / X"
                    value={`@${brand.twitterHandle}`}
                    href={`https://x.com/${brand.twitterHandle}`}
                    external
                  />
                )}
                {brand.linkedinUrl && (
                  <ContactLink
                    label="LinkedIn"
                    value={brand.linkedinUrl.replace(/^https?:\/\/(www\.)?/, '')}
                    href={brand.linkedinUrl}
                    external
                  />
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="mt-8">
        <Link to="/deals" search={{ status: undefined, page: 1 }}>
          <Button variant="outline">View my deals with {brand.name}</Button>
        </Link>
      </div>
    </PageTransition>
  );
}

type BrandWithContacts = {
  partnershipEmail: string | null;
  prEmail: string | null;
  nilContactUrl: string | null;
  pressPageUrl: string | null;
  instagramHandle: string | null;
  twitterHandle: string | null;
  linkedinUrl: string | null;
};

function hasAnyContact(b: BrandWithContacts): boolean {
  return !!(
    b.partnershipEmail ||
    b.prEmail ||
    b.nilContactUrl ||
    b.pressPageUrl ||
    b.instagramHandle ||
    b.twitterHandle ||
    b.linkedinUrl
  );
}

// ---------------------------------------------------------------------------
// Match breakdown (US-023.7)
// ---------------------------------------------------------------------------

type MatchBreakdown = {
  total: number;
  dimensions: {
    values: MatchDim;
    sport: MatchDim;
    interest: MatchDim;
    dealType: MatchDim;
    dealSize: MatchDim;
  };
};
type MatchDim = {
  score: number;
  points: number;
  max: number;
  overlap: string[];
  hasSignal: boolean;
};

function MatchBreakdownCard({ breakdown }: { breakdown: MatchBreakdown }) {
  const t = Math.round(breakdown.total);
  const headlineClass =
    t >= 80 ? 'text-primary' : t >= 60 ? 'text-primary/90' : t >= 40 ? '' : 'text-muted-foreground';
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle>Your Match Score</CardTitle>
          <CardDescription>
            How well this brand aligns with the values and preferences on your profile
          </CardDescription>
        </div>
        <div className={`text-4xl font-bold tabular-nums ${headlineClass}`}>{t}%</div>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3">
          <MatchRow
            label="Values alignment"
            subtitle={
              breakdown.dimensions.values.overlap.length > 0
                ? `Shared: ${breakdown.dimensions.values.overlap.join(', ')}`
                : breakdown.dimensions.values.hasSignal
                  ? 'No values overlap yet'
                  : 'Add personal values to your profile'
            }
            dim={breakdown.dimensions.values}
          />
          <MatchRow
            label="Sport alignment"
            subtitle={
              breakdown.dimensions.sport.hasSignal
                ? breakdown.dimensions.sport.overlap.length > 0
                  ? `Matches your sport: ${breakdown.dimensions.sport.overlap.join(', ')}`
                  : 'Your sport is not in this brand\u2019s focus'
                : 'Set your sport on your profile'
            }
            dim={breakdown.dimensions.sport}
          />
          <MatchRow
            label="Interests / industries"
            subtitle={
              breakdown.dimensions.interest.overlap.length > 0
                ? `Shared: ${breakdown.dimensions.interest.overlap.join(', ')}`
                : breakdown.dimensions.interest.hasSignal
                  ? 'No overlap with this brand\u2019s categories'
                  : 'Add interests / preferred industries to your profile'
            }
            dim={breakdown.dimensions.interest}
          />
          <MatchRow
            label="Deal type compatibility"
            subtitle={
              breakdown.dimensions.dealType.overlap.length > 0
                ? `Compatible: ${breakdown.dimensions.dealType.overlap.join(', ')}`
                : breakdown.dimensions.dealType.hasSignal
                  ? 'None of your preferred deal types match'
                  : 'Pick preferred deal types on your profile'
            }
            dim={breakdown.dimensions.dealType}
          />
          <MatchRow
            label="Deal size fit"
            subtitle={
              breakdown.dimensions.dealSize.overlap.length > 0
                ? breakdown.dimensions.dealSize.overlap[0]
                : breakdown.dimensions.dealSize.hasSignal
                  ? 'Brand\u2019s range is below your minimum'
                  : 'Set a minimum deal size on your profile'
            }
            dim={breakdown.dimensions.dealSize}
          />
        </ul>
      </CardContent>
    </Card>
  );
}

function MatchRow({ label, subtitle, dim }: { label: string; subtitle: string; dim: MatchDim }) {
  // Render a horizontal progress bar for this dimension. Dim the row when
  // the athlete hasn't provided input — the CTA goes in the subtitle.
  const pct = Math.round((dim.points / dim.max) * 100);
  const barColor = !dim.hasSignal
    ? 'bg-muted'
    : pct >= 75
      ? 'bg-primary'
      : pct >= 40
        ? 'bg-primary/60'
        : 'bg-primary/30';
  return (
    <li className={`space-y-1 ${!dim.hasSignal ? 'opacity-70' : ''}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-xs text-muted-foreground tabular-nums">
          {dim.points}/{dim.max}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all`}
          style={{ width: `${pct}%` }}
          aria-hidden="true"
        />
      </div>
      <p className="text-xs text-muted-foreground">{subtitle}</p>
    </li>
  );
}

function ContactLink({
  label,
  value,
  href,
  external,
}: {
  label: string;
  value: string;
  href: string;
  external?: boolean;
}) {
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className="block rounded-md border p-3 hover:bg-secondary/50 transition-colors"
    >
      <p className="text-xs font-medium uppercase text-muted-foreground">{label}</p>
      <p className="text-sm mt-0.5 break-all">{value}</p>
    </a>
  );
}
